import { useState, useEffect } from "react";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { todayISO } from "./helpers";

const ROUNDING = [["two_dp", "Two decimal places"], ["nearest_025", "Nearest 0.25 hour"], ["nearest_05", "Nearest 0.5 hour"], ["up_05", "Round up to next 0.5 hour"]];

const DEFAULTS = {
  name: "", start_date: "", end_date: "", default_allowance_weeks: 5, bank_holiday_treatment: "additional",
  region: "england-and-wales", week_start_day: "monday", proration_method: "monthly", rounding_method: "two_dp",
  pending_reserves: true, allow_negative: false,
};

const F = ({ label, children }) => <div><Label className="text-xs text-slate-500 mb-1 block">{label}</Label>{children}</div>;

function CarryForwardCard({ years }) {
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  useEffect(() => {
    if (!years.length) return;
    const today = todayISO();
    const active = years.find((y) => y.start_date <= today && y.end_date >= today) || years[0];
    setSource((s) => s || active.id);
    setTarget((t) => t || (years.find((y) => y.id !== active.id)?.id || ""));
  }, [years]);
  const run = async () => {
    if (!source || !target || source === target) { toast.error("Choose two different holiday years"); return; }
    try {
      const { data } = await api.post("/holiday-years/carry-forward", { source_year_id: source, target_year_id: target });
      toast.success(`Carried forward: ${data.created} created, ${data.updated} updated`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="carry-forward-card">
      <h3 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><ArrowRightLeft size={18} />Carry forward remaining hours</h3>
      <p className="text-sm text-slate-500 mt-1 mb-4">Copy each active employee's confirmed remaining hours from one holiday year into the next as an auditable carry-forward adjustment. Re-running updates existing carry-forward entries rather than duplicating them.</p>
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <p className="text-xs text-slate-500 mb-1">From year</p>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-40" data-testid="cf-source-select"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <ArrowRightLeft size={16} className="mb-2 text-slate-400" />
        <div>
          <p className="text-xs text-slate-500 mb-1">Into year</p>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger className="w-40" data-testid="cf-target-select"><SelectValue placeholder="Target" /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={run} className="bg-[#0B1A2F] hover:bg-[#162B48]" data-testid="run-carry-forward-btn">Carry forward</Button>
      </div>
    </div>
  );
}

function YearDialog({ onSaved, existing }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(existing || DEFAULTS);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...f, default_allowance_weeks: Number(f.default_allowance_weeks) };
      if (existing) await api.put(`/holiday-years/${existing.id}`, payload);
      else await api.post("/holiday-years", payload);
      toast.success("Saved"); setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {existing ? <Button variant="outline" size="sm" data-testid={`edit-year-${existing.id}`}>Edit</Button>
          : <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="add-year-btn"><Plus size={16} className="mr-2" />Add holiday year</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{existing ? "Edit" : "New"} holiday year</DialogTitle><DialogDescription>Set the leave year's dates, default allowance and calculation rules.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="2027/28" data-testid="year-name-input" /></F>
          <F label="Default allowance (weeks)"><Input type="number" step="0.5" value={f.default_allowance_weeks} onChange={(e) => set("default_allowance_weeks", e.target.value)} /></F>
          <F label="Start date"><Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="year-start-input" /></F>
          <F label="End date"><Input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="year-end-input" /></F>
          <F label="Proration method">
            <Select value={f.proration_method} onValueChange={(v) => set("proration_method", v)}>
              <SelectTrigger data-testid="year-proration-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="monthly">Complete-month</SelectItem><SelectItem value="calendar">Calendar-day</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Rounding method">
            <Select value={f.rounding_method} onValueChange={(v) => set("rounding_method", v)}>
              <SelectTrigger data-testid="year-rounding-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROUNDING.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Bank-holiday treatment">
            <Select value={f.bank_holiday_treatment} onValueChange={(v) => set("bank_holiday_treatment", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="additional">Additional</SelectItem><SelectItem value="included">Included</SelectItem><SelectItem value="excluded">Excluded</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Region">
            <Select value={f.region} onValueChange={(v) => set("region", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="england-and-wales">England & Wales</SelectItem><SelectItem value="scotland">Scotland</SelectItem><SelectItem value="northern-ireland">Northern Ireland</SelectItem></SelectContent>
            </Select>
          </F>
        </div>
        <div className="space-y-2 border-t pt-3">
          <label className="flex items-center justify-between text-sm"><span>Pending requests reserve holiday</span><Switch checked={f.pending_reserves} onCheckedChange={(v) => set("pending_reserves", v)} /></label>
          <label className="flex items-center justify-between text-sm"><span>Allow requesting more than balance</span><Switch checked={f.allow_negative} onCheckedChange={(v) => set("allow_negative", v)} /></label>
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#2EA3FD]" data-testid="year-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function YearCard({ year, onSaved }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex justify-between items-center flex-wrap gap-3" data-testid={`year-card-${year.id}`}>
      <div>
        <p className="font-semibold text-[#0B1A2F]">{year.name} {year.closed && <span className="text-xs text-slate-400">(closed)</span>}</p>
        <p className="text-xs text-slate-500">{fmtDate(year.start_date)} – {fmtDate(year.end_date)} · {year.default_allowance_weeks} weeks · {year.proration_method} proration · {year.rounding_method} · BH {year.bank_holiday_treatment}</p>
      </div>
      <YearDialog existing={year} onSaved={onSaved} />
    </div>
  );
}

export default function LeaveYearsCard() {
  const [years, setYears] = useState([]);
  const loadYears = () => api.get("/holiday-years").then((r) => setYears(r.data));
  useEffect(() => { loadYears(); }, []);
  return (
    <div className="space-y-4" data-testid="leave-years-card">
      <div className="flex justify-end"><YearDialog onSaved={loadYears} /></div>
      {years.map((y) => <YearCard key={y.id} year={y} onSaved={loadYears} />)}
      <CarryForwardCard years={years} />
    </div>
  );
}
